import customFetch from "./api";
import {
  SEARCH_MODULES,
  normalizeSearchResult,
  getAllModuleKeys,
} from "components/shared/global-search/searchConfig";

/**
 * Search a single module/category
 * @param {string} moduleKey - The module key (e.g., "customers", "invoices")
 * @param {string} query - The search query string
 * @returns {Promise<Array>} - Array of normalized search results
 */
export const searchSingleModule = async (moduleKey, query) => {
  const module = SEARCH_MODULES[moduleKey];
  if (!module) {
    console.warn(`Module ${moduleKey} not found in configuration`);
    return [];
  }

  try {
    const endpoint = `${module.endpoint}?search=${encodeURIComponent(query || "")}`;
    const response = await customFetch(endpoint);

    if (response.status && response.data) {
      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      // Normalize each result to consistent format
      return items.map((item) => normalizeSearchResult(item, moduleKey));
    }

    return [];
  } catch (error) {
    console.error(`Error searching ${moduleKey}:`, error);
    return [];
  }
};

/**
 * Search all modules in parallel
 * @param {string} query - The search query string
 * @returns {Promise<Object>} - Object with module keys as keys and arrays of results as values
 */
export const searchAllModules = async (query) => {
  const moduleKeys = getAllModuleKeys();

  // Fire all requests in parallel using Promise.allSettled
  // This ensures one failure doesn't break everything
  const searchPromises = moduleKeys.map(async (moduleKey) => {
    try {
      const results = await searchSingleModule(moduleKey, query);
      return { moduleKey, results, success: true };
    } catch (error) {
      console.error(`Error searching ${moduleKey}:`, error);
      return { moduleKey, results: [], success: false };
    }
  });

  const settledResults = await Promise.allSettled(searchPromises);

  // Combine successful results into a single object organized by module
  const combinedResults = {};

  settledResults.forEach((result) => {
    if (result.status === "fulfilled") {
      const { moduleKey, results } = result.value;
      combinedResults[moduleKey] = results;
    } else {
      // Handle rejected promises
      console.error("Search promise rejected:", result.reason);
    }
  });

  return combinedResults;
};

/**
 * Search a subset of modules in parallel
 * @param {string[]} moduleKeys - Array of module keys (e.g., ["customers", "invoices"])
 * @param {string} query - The search query string
 * @returns {Promise<Object>} - Object with module keys as keys and arrays of results as values
 */
export const searchSelectedModules = async (moduleKeys, query) => {
  if (!Array.isArray(moduleKeys) || moduleKeys.length === 0) {
    return {};
  }

  const searchPromises = moduleKeys.map(async (moduleKey) => {
    try {
      const results = await searchSingleModule(moduleKey, query);
      return { moduleKey, results, success: true };
    } catch (error) {
      console.error(`Error searching ${moduleKey}:`, error);
      return { moduleKey, results: [], success: false };
    }
  });

  const settledResults = await Promise.allSettled(searchPromises);
  const combinedResults = {};

  settledResults.forEach((result) => {
    if (result.status === "fulfilled") {
      const { moduleKey, results } = result.value;
      combinedResults[moduleKey] = results;
    } else {
      console.error("Search promise rejected:", result.reason);
    }
  });

  return combinedResults;
};
